/**
 * SkerpaAutoContext — Automatically pushes page-level context to Skerpa
 * based on the active view. Lives in MainLayout so it covers all views.
 *
 * Each view gets a thin context builder that reads from TanStack Query
 * or Zustand stores. Views that don't have a builder get nothing
 * (the viewHints in useSkerpaContext already provide basic context).
 *
 * Settings and help views are excluded — Skerpa doesn't need their data.
 */

import { useMemo, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { useEntityListener } from '@/hooks/useEntityListener'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { useInterviewerStore } from '@/stores/interviewerStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { personaWorkshopApi, competitorMapApi, solutionRequirementsApi, requirementsApi, marketSegmentationApi } from '@/lib/api/client'
import type { Competitor } from '@ispot/shared'
import {
  buildPersonaContext,
  buildInterviewContext,
  buildJourneyContext,
  buildCompetitorContext,
  buildSegmentationContext,
} from '@/lib/skerpa/contextBuilders'

// Views that should NOT push context
const EXCLUDED_VIEWS = new Set(['settings', 'help'])

// Views that push their own rich context manually via useSkerpaPageContext
// SkerpaAutoContext skips these to avoid overwriting their detailed data
const MANUAL_CONTEXT_VIEWS = new Set(['value-design', 'canvas-market', 'canvas-business', 'canvas-solution'])

export function SkerpaAutoContext() {
  const activeView = useSkerpaContextStore(s => s.activeView)
  const activeResourceId = useSkerpaContextStore(s => s.activeResourceId)
  const setPageContext = useSkerpaContextStore(s => s.setPageContext)
  const project = useProjectStore(s => s.currentProject)
  const account = useAuthStore(s => s.currentAccount)

  const accountId = account?.id || ''
  const projectId = project?.id || ''
  const hasIds = !!(accountId && projectId)

  // ── Interview / transcript data (from Zustand store, no API call needed) ──
  const isInterviewView = activeView === 'interviewer'
  const currentSource = useInterviewerStore(s => s.currentSource)

  // ── Journey data (from Zustand store — workspace, discovery, analysis) ──
  const isJourneyView = activeView === 'workspace' || activeView === 'discovery' || activeView === 'analysis'
  const framework = useJourneyStore(s => s.framework)
  const journey = useJourneyStore(s => s.journey)
  const getCurrentPhase = useJourneyStore(s => s.getCurrentPhase)
  const getActivePhases = useJourneyStore(s => s.getActivePhases)
  const getDomainScores = useJourneyStore(s => s.getDomainScores)
  const getOverallProgress = useJourneyStore(s => s.getOverallProgress)
  const isActivityCompleted = useJourneyStore(s => s.isActivityCompleted)

  // ── Persona data (for persona-configurator + persona-workshop) ──
  const isPersonaView = activeView === 'persona-configurator' || activeView === 'persona-workshop'
  const { data: personaSessions } = useQuery({
    queryKey: ['persona-sessions', accountId, projectId],
    queryFn: () => personaWorkshopApi.listSessions(accountId, projectId),
    enabled: hasIds && isPersonaView,
    staleTime: 30_000,
  })

  // ── Competitor data ──
  const isCompetitorView = activeView === 'competitor-map'
  const { data: competitorData } = useQuery({
    queryKey: ['competitors', accountId, projectId],
    queryFn: () => competitorMapApi.listCompetitors(accountId, projectId),
    enabled: hasIds && isCompetitorView,
    staleTime: 30_000,
  })

  // ── Market segmentation data ──
  // Also fetched on persona view so Skerpa can surface segments without personas
  const isSegmentationView = activeView === 'market-segmentation'
  const { data: segmentationData } = useQuery({
    queryKey: ['market-segmentation-canvas', accountId, projectId],
    queryFn: () => marketSegmentationApi.getCanvas(accountId, projectId),
    enabled: hasIds && (isSegmentationView || isPersonaView),
    staleTime: 30_000,
  })

  // Canvas views (canvas-market, canvas-business, canvas-solution) push their own context
  // via CanvasExplorer's manual setPageContext call — no auto-context needed

  // ── Requirements PRD data ──
  const isRequirementsView = activeView === 'requirements'
  const { data: requirementsData } = useQuery({
    queryKey: ['requirements', accountId, projectId],
    queryFn: () => requirementsApi.list(accountId, projectId),
    enabled: hasIds && isRequirementsView,
    staleTime: 30_000,
  })

  // ── Solution configurator data ──
  const isSolutionView = activeView === 'solution-configurator'
  const { data: solutionData } = useQuery({
    queryKey: ['solution-requirements', accountId, projectId],
    queryFn: () => solutionRequirementsApi.getTree(accountId, projectId),
    enabled: hasIds && isSolutionView,
    staleTime: 30_000,
  })

  // Refresh view-specific queries when entities change
  const queryClient = useQueryClient()
  const refreshPersonas = useCallback(() => { queryClient.invalidateQueries({ queryKey: ['persona-sessions'] }) }, [queryClient])
  const refreshCompetitors = useCallback(() => { queryClient.invalidateQueries({ queryKey: ['competitors'] }) }, [queryClient])
  const refreshSolution = useCallback(() => { queryClient.invalidateQueries({ queryKey: ['solution-requirements'] }) }, [queryClient])
  useEntityListener('entity-created', 'Persona', refreshPersonas)
  useEntityListener('entity-updated', 'Persona', refreshPersonas)
  useEntityListener('entity-deleted', 'Persona', refreshPersonas)
  useEntityListener('entity-created', 'Competitor', refreshCompetitors)
  useEntityListener('entity-updated', 'Competitor', refreshCompetitors)
  useEntityListener('entity-deleted', 'Competitor', refreshCompetitors)
  useEntityListener('entity-created', 'Feature', refreshSolution)
  useEntityListener('entity-updated', 'Feature', refreshSolution)
  useEntityListener('entity-deleted', 'Feature', refreshSolution)

  // Build context string based on active view
  const context = useMemo(() => {
    if (EXCLUDED_VIEWS.has(activeView) || MANUAL_CONTEXT_VIEWS.has(activeView)) return ''

    // Persona views
    if (isPersonaView && personaSessions) {
      const sessions = Array.isArray(personaSessions) ? personaSessions : []
      const segmentNames = ((segmentationData as any)?.segments || []).map((s: { name: string }) => s.name) as string[]
      if (sessions.length === 0 && segmentNames.length === 0) return 'No personas created yet.'
      if (sessions.length === 0) return `No personas created yet.\nSegments without a linked persona: ${segmentNames.join(', ')}`
      return buildPersonaContext(sessions, segmentNames, activeResourceId)
    }

    // Competitor map (auto-context replaces the manual one in CompetitorMapPage)
    if (isCompetitorView && competitorData) {
      const competitors: Competitor[] = Array.isArray(competitorData) ? competitorData : competitorData?.competitors || []
      if (competitors.length === 0) return 'No competitors tracked yet.'
      return buildCompetitorContext(competitors)
    }

    // Market segmentation
    if (isSegmentationView && segmentationData) {
      return buildSegmentationContext(segmentationData)
    }

    // Requirements PRD
    if (isRequirementsView) {
      const reqs = Array.isArray(requirementsData) ? requirementsData : []
      if (reqs.length === 0) return 'User is on the Requirements page. No requirements created yet. They can generate a PRD hierarchy from their product intelligence or add requirements manually.'
      const themes = reqs.filter((r: any) => r.depth === 0)
      const total = reqs.length
      const byStatus: Record<string, number> = {}
      for (const r of reqs) { byStatus[r.status] = (byStatus[r.status] || 0) + 1 }
      let ctx = `User is on the Requirements page (PRD builder). ${total} requirements across ${themes.length} themes.\n`
      ctx += `Status: ${Object.entries(byStatus).map(([s, n]) => `${n} ${s}`).join(', ')}\n\nThemes:\n`
      for (const t of themes) {
        const children = reqs.filter((r: any) => r.parentId === t.id)
        ctx += `- ${t.name} (${children.length} children)\n`
      }
      return ctx
    }

    // Solution configurator
    if (isSolutionView && solutionData) {
      const tree = solutionData as { requirements?: { title: string; status?: string; children?: unknown[] }[] }
      const reqs = tree?.requirements || []
      if (reqs.length === 0) return 'No solution requirements defined yet.'
      return `Solution Requirements: ${reqs.length} top-level\n${reqs.slice(0, 10).map((r: { title: string; status?: string }) => `  - ${r.title} [${r.status || 'draft'}]`).join('\n')}${reqs.length > 10 ? `\n  ... and ${reqs.length - 10} more` : ''}`
    }

    // Interview / transcript analysis
    if (isInterviewView && currentSource) {
      return buildInterviewContext(currentSource)
    }

    // Journey-aware views (workspace, discovery, analysis)
    if (isJourneyView && framework && journey) {
      return buildJourneyContext(activeView, framework, journey, getCurrentPhase, getActivePhases, getDomainScores, getOverallProgress, isActivityCompleted)
    }

    return ''
  }, [activeView, activeResourceId, isPersonaView, personaSessions, segmentationData, isCompetitorView, competitorData, isSegmentationView, isRequirementsView, requirementsData, isSolutionView, solutionData, isInterviewView, currentSource, isJourneyView, framework, journey, getCurrentPhase, getActivePhases, getDomainScores, getOverallProgress, isActivityCompleted])

  // Push to store
  useEffect(() => {
    if (context) setPageContext(context)
  }, [context, setPageContext])

  return null // renderless component
}
